/**
 * Anzeigename fuer ein anonymes Konto.
 *
 * Ohne ihn stand auf der Startseite "Hallo, !". Seit Anonymous-First hat der
 * haeufigste Besucher gar keinen Namen - der Platzhalter macht die Anrede
 * wieder vollstaendig, ohne nach einem Namen zu fragen.
 */
export const GUEST_DISPLAY_NAME = 'Gast';

/**
 * Steht im Profil ein selbst gesetzter Anzeigename?
 *
 * Geprueft wird genau zweierlei: dass ueberhaupt etwas dasteht, und dass es
 * nicht unser Platzhalter ist. Ob der Name "echt" ist, spielt keine Rolle -
 * "Jabby123" ist eine Identifikation wie jede andere, und die App hat nicht zu
 * beurteilen, wie jemand heissen moechte.
 *
 * Gebraucht wird das an genau einer Stelle: nach der Registrierung soll der
 * Namensdialog erscheinen. Eine blosse Pruefung auf "leer" wuerde ihn seit dem
 * Platzhalter verschlucken, und der Nutzer hiesse fuer immer Gast.
 */
export function hasOwnDisplayName(name: string | null | undefined): boolean {
  const wert = (name ?? '').trim();
  return wert.length > 0 && wert !== GUEST_DISPLAY_NAME;
}


import { SongTier } from "./Song";

export type UserRole = "user" | "supporter" | "admin";

export interface UserCreditsData {
  songBasic: number;
  songPremium: number;
  songExclusive: number;
}

export interface UserStatsData {
  variantsCreated: number;
  songBasicUsed: number;
  songPremiumUsed: number;
  songExclusiveUsed: number;
  lastSongAt?: string | null;
}

export interface UserProfileData {
  id: string;
  displayName: string;
  email?: string | null;
  photoURL?: string | null;

  role: UserRole;

  credits: UserCreditsData;
  stats: UserStatsData;

  referrerCode?: string | null;
  referredByUserId?: string | null;

  /**
   * Ausgegebene Rabattcodes aus Bewertungen - geschrieben von submitSongRating.
   *
   * Der Code steht auch auf der Dankesseite, aber wer ihn dort nicht abschreibt,
   * findet ihn nie wieder. Das Profil ist der einzige Ort, den ein Nutzer von
   * sich aus wieder ansteuert.
   *
   * Ob ein Code schon eingeloest ist, weiss nur Stripe - deshalb steht am Code
   * "einmal einloesbar" und nicht "noch gueltig".
   */
  rewardCodes?: RewardCode[];

  createdAt: string;
  updatedAt: string;
}

export interface RewardCode {
  code: string;
  songId: string;
  percent: number;
  createdAt: string;
}

export class UserProfile {
  readonly id: string;
  displayName: UserProfileData["displayName"];
  email?: UserProfileData["email"];
  photoURL?: UserProfileData["photoURL"];

  role: UserProfileData["role"];

  credits: UserProfileData["credits"];
  stats: UserProfileData["stats"];

  referrerCode?: UserProfileData["referrerCode"];
  referredByUserId?: UserProfileData["referredByUserId"];
  rewardCodes?: UserProfileData["rewardCodes"];

  createdAt: UserProfileData["createdAt"];
  updatedAt: UserProfileData["updatedAt"];

  constructor(data: UserProfileData) {
    this.id = data.id;
    this.displayName = data.displayName ?? null;
    this.email = data.email ?? null;
    this.photoURL = data.photoURL ?? null;
    this.role = data.role;
    this.credits = data.credits;
    this.stats = data.stats;
    this.referrerCode = data.referrerCode ?? null;
    this.referredByUserId = data.referredByUserId ?? null;
    this.rewardCodes = data.rewardCodes ?? [];
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
  }

  static init(aId = "", aName = "", aEmail: null | string = null): UserProfile {
    const now = new Date().toISOString();
    const randomAvatarIndex = Math.floor(Math.random() * 4) + 1;
    const data: UserProfileData = {
      id: aId,
      createdAt: now,
      updatedAt: now,
      displayName: aName,
      email: aEmail,
      photoURL: 'noavatar_0' + randomAvatarIndex + '.png',
      role: "user",
      credits: { songBasic: 0, songPremium: 0, songExclusive: 0 },
      stats: { variantsCreated: 0, songBasicUsed: 0, songPremiumUsed: 0, songExclusiveUsed: 0, lastSongAt: null }
    };
    return new UserProfile(data);
  }

  static initFromObject(o: UserProfileData): UserProfile {
    if (!o || typeof o !== "object") {
      throw new Error("Invalid UserData: empty or not an object");
    }
    if (!o.id || !o.createdAt || !o.updatedAt || !o.role || !o.credits || !o.stats) {
      throw new Error("Invalid UserData: missing required fields");
    }
    return new UserProfile(o);
  }

  getDataObject(): UserProfileData {
    return {
      id: this.id,
      displayName: this.displayName ?? null,
      email: this.email ?? null,
      photoURL: this.photoURL ?? null,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      role: this.role,
      credits: this.credits,
      stats: this.stats,
      referrerCode: this.referrerCode ?? null,
      referredByUserId: this.referredByUserId ?? null
    };
  }

  setDisplayName(aName: string) {
    this.displayName = aName;
  }
  setPhotoURL(aUrl: string) {
    this.photoURL = aUrl;
  }

  touchUpdated() {
    this.updatedAt = new Date().toISOString();
  }

  convertAnonUser(aName: string, aEmail: string) {
    this.displayName = aName;
    this.email = aEmail;
  }

  /**
   * Name fuer eine Anrede.
   *
   * Der Rueckfall deckt die Profile ab, die vor Einfuehrung des Platzhalters
   * angelegt wurden - deren displayName ist leer, und "Hallo, !" bliebe sonst
   * fuer immer stehen.
   */
  get greetingName(): string {
    return this.displayName || GUEST_DISPLAY_NAME;
  }

  isGuest(): boolean {
    return this.email?.length == 0;
  }

  getAvailableTiers(): SongTier[] {
  const tiers: SongTier[] = [];
  if (this.credits.songBasic > 0) tiers.push("basic");
  if (this.credits.songPremium > 0) tiers.push("premium");
  if (this.credits.songExclusive > 0) tiers.push("exclusive");
  return tiers;
}
}

export interface PublicUserData {
  displayName: string;
  photoURL?: string | null;
  role: UserRole;
  stats: UserStatsData;
}

export class PublicUserProfile {
  displayName: string;
  photoURL?: string | null;
  role: UserRole;
  stats: UserStatsData;

  constructor(data: PublicUserData) {
    this.displayName = data.displayName;
    this.photoURL = data.photoURL;
    this.role = data.role;
    this.stats = data.stats;
  }

  getDataObject(): PublicUserData {
    return {
      displayName: this.displayName,
      photoURL: this.photoURL,
      role: this.role,
      stats: this.stats,
    };
  }

  static initFromObject(o: PublicUserData): PublicUserProfile {
    if (!o || typeof o !== "object") {
      throw new Error("Invalid PublicUserData: empty or not an object");
    }
    return new PublicUserProfile(o);
  }
}
